/* eslint-disable no-negated-condition */
import {Alert, Button, message, Modal, Divider, Checkbox} from "antd";
import {CheckboxChangeEvent} from "antd/lib/checkbox";
import {QueryOptions} from "@apollo/client";
import React, {FC, memo, useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import uuid from "uuid";
import {
    GetBookingsByCourseDocument,
    GetBookinsDocument,
    GetBookingsOverviewOptimizedDocument,
    GetCourseByIdDocument,
    GetSettledBookingsDocument,
    GetUnsettledBookingsDocument,
    PaymentProvider,
    useCancelBookingAndGenerateReversalInvoiceMutation,
    useGetAppSettingsQuery,
    useGetBookingByIdQuery,
    useRefundMolliePaymentFromAdminMutation,
} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import {TableType} from "./BookingListColumns";
import css from "./ReverseBookingModal.less";
import {GET_BOOKINS, GET_BOOKINGS_OVERVIEW_OPTIMIZED} from "../bookings.graphql";
import {GET_SETTLED_BOOKINGS, GET_UNSETTLED_BOOKINGS} from "../../invoice/invoice.graphql";
import {URL_SETTINGS_GENERAL} from "../../../models/url";
import {colorGrey} from "../../../styles/colors";
import RefundPanel, {RefundPanelRow, RefundChoice} from "../../../views/RefundPanel/RefundPanel";

type ReverseBookingModalProps = {
    open: boolean;
    closeModal: () => void;
    record: TableType;
    confirmAction?: (e: boolean) => void;
    refetch?: () => void;
};

const ReverseBookingModal: React.FC<ReverseBookingModalProps> = ({open = false, closeModal, record, confirmAction = (e) => console.log(e), refetch}) => {
    const [sendReversalInvoice, setSendReversalInvoice] = useState(false);
    const {data: appSettingsData} = useGetAppSettingsQuery();

    useEffect(() => {
        if (appSettingsData) {
            setSendReversalInvoice(appSettingsData.settings?.sendReversalInvoice || false);
        }
    }, [appSettingsData]);

    const invoiceId = record.invoice?.id;

    // Phase 14 / Plan 14-04: ScheduledPayments für RefundPanel laden —
    // TableType (aus GetBookingsOverviewOptimized) enthält nur paymentProvider + mollieMethod,
    // wir brauchen zusätzlich id, paymentAmount, refundedAmount, paymentDue, paymentDone, title.
    const {data: bookingByIdData} = useGetBookingByIdQuery({
        variables: {id: record.id},
        skip: !open || !record.id,
        fetchPolicy: "cache-and-network",
    });

    // D-07: pendingRefunds wird vom Panel via onSelectionChange befüllt und
    // beim handleOk VOR dem Storno-Mutation-Call sequentiell ausgeführt
    const [pendingRefunds, setPendingRefunds] = useState<{
        refunds: Array<{scheduledPaymentId: string; amount: number; kind: RefundChoice}>;
        reason: string;
        complete: boolean;
    } | null>(null);
    const [refundMolliePayment] = useRefundMolliePaymentFromAdminMutation();

    // D-15 / D-12: refund-fähige Mollie-SPs extrahieren (Mollie + bezahlt + Restbetrag > 0)
    // Bug-Fix Mollie-Refund-Cap: Cap = (molliePaidAmount ?? paymentAmount) - refundedAmount.
    // molliePaidAmount kann null/undefined sein → Fallback auf paymentAmount (= altes
    // Verhalten `refundedAmount < paymentAmount`). Voll-erstattete Raten (Cap = 0) werden
    // gar nicht erst ans Panel übergeben, damit keine "Ghost-Row" entsteht.
    const mollieRefundableSps: RefundPanelRow[] = useMemo(() => {
        const sps = bookingByIdData?.bookingById?.scheduledPayments ?? [];
        return sps
            .filter(
                (sp) =>
                    sp.paymentProvider === PaymentProvider.Mollie
                    && sp.paymentDone !== null
                    && sp.paymentDone !== undefined
                    && (sp.molliePaidAmount ?? sp.paymentAmount) - Number(sp.refundedAmount || 0) > 0,
            )
            .map((sp) => ({
                scheduledPaymentId: sp.id,
                paymentAmount: Number(sp.paymentAmount),
                refundedAmount: Number(sp.refundedAmount || 0),
                paymentDue: sp.paymentDue,
                paymentDone: sp.paymentDone ?? null,
                title: sp.title ?? null,
                paymentProvider: sp.paymentProvider,
                mollieMethod: sp.mollieMethod,
                molliePaidAmount: sp.molliePaidAmount,
            }));
    }, [bookingByIdData]);

    const confirmDeletion = (confirmed: boolean) => {
        confirmAction(confirmed);
    };

    const refetchQueries: Array<string | QueryOptions> = [
        "GetSettledBookingsDocument",
        {query: GetSettledBookingsDocument},
        "GetUnsettledBookingsDocument",
        {query: GetUnsettledBookingsDocument},
        "GetBookins",
        {query: GetBookinsDocument},
        "GetBookinsQuery",
        {query: GET_BOOKINS},
        "GetBookingsOverviewOptimized",
        {query: GetBookingsOverviewOptimizedDocument},
        "GetBookingsOverviewOptimizedQuery",
        {query: GET_BOOKINGS_OVERVIEW_OPTIMIZED},
        "GetUnsettledBookings",
        {query: GET_UNSETTLED_BOOKINGS},
        "GetSettledBookings",
        {query: GET_SETTLED_BOOKINGS},
    ];

    // Only pushing this for CourseDetails, because we need to refetch the bookings for the specific course
    // If we are on the course details page otherwise there is no need to refetch the bookings
    if (record.courseId) {
        refetchQueries.push({
            query: GetBookingsByCourseDocument,
            variables: {id: record.courseId},
        });
        refetchQueries.push({
            query: GetCourseByIdDocument,
            variables: {id: record.courseId},
        });
    }

    const [cancelBookingAndCreateReversal, {data, loading}] = useCancelBookingAndGenerateReversalInvoiceMutation({
        awaitRefetchQueries: true,
        refetchQueries,
    });
    const email = record.attendee?.booker?.email;
    // Ohne Vorauswahl im Panel: Stornieren erst, wenn für jede bezahlte Mollie-Rate entschieden ist.
    const refundChoiceMissing = mollieRefundableSps.length > 0 && !pendingRefunds?.complete;
    const handleOk = async () => {
        if (refundChoiceMissing) return;
        if (invoiceId) {
            const sendMail = email !== "" && email !== null && email !== undefined && sendReversalInvoice;
            // Eine Korrelations-ID für die ganze Storno-Aktion (Refunds + Storno).
            // Der Server übernimmt den Header, sodass „Buchung storniert" und die
            // „Erstattung veranlasst"-Einträge in der Historie als EIN Block erscheinen.
            const correlationContext = {headers: {"x-correlation-id": uuid()}};

            try {
                // Phase 14 / D-07: Refunds VOR Storno sequentiell ausführen.
                // Bei Fehler bleibt das Modal offen (Throw blockiert cancelBooking).
                if (pendingRefunds && pendingRefunds.refunds.length > 0) {
                    for (const refund of pendingRefunds.refunds) {
                        if (refund.kind === "none") continue;
                        await refundMolliePayment({
                            variables: {
                                scheduledPaymentId: refund.scheduledPaymentId,
                                amount: refund.amount,
                                reason: pendingRefunds.reason || undefined,
                            },
                            context: correlationContext,
                        });
                    }
                }

                await cancelBookingAndCreateReversal({
                    variables: {id: invoiceId, sendMail},
                    context: correlationContext,
                })
                    .then(() => confirmDeletion(true))
                    .then(() => refetch?.())
                    .then(() => closeModal())
                    .then(() => message.success(`${i18n.containers.bookings.BookingList.bookingReversal.messageSuccess()}`, 3));
            } catch (error) {
                // Bug-Fix: zuvor wurde fälschlich cancelBookingAndCreateReversalError
                // (aus dem Mutation-Hook) geloggt — der eigentliche Fehler kann aber
                // auch aus dem Refund-Schritt stammen. Jetzt das real gefangene error loggen.
                console.error("cancelBookingAndCreateReversal error: ", error);
                message.error(`Refund/Storno fehlgeschlagen: ${error instanceof Error ? error.message : String(error)}`, 5);
                throw new Error(`Error on cancel booking: ${error}`);
            }
        }
    };

    const handleCancel = () => {
        refetch?.();
        confirmDeletion(false);
        closeModal();
    };

    const NotificationSelection = ({style}: {style?: React.CSSProperties}) => {
        const handleChange = (e: CheckboxChangeEvent) => {
            const checked = e.target.checked;
            const appSettings = appSettingsData?.settings.sendReversalInvoice;

            if (appSettings === true && checked === true) {
                setSendReversalInvoice(false);
            } else if (appSettings === true && checked === false) {
                setSendReversalInvoice(true);
            } else if (appSettings === false && checked === true) {
                setSendReversalInvoice(true);
            } else {
                setSendReversalInvoice(false);
            }
        };

        const checked = sendReversalInvoice !== appSettingsData?.settings?.sendReversalInvoice;

        const actualStyle = {
            width: "100%",
            display: "flex",
            justifyContent: "center",
            ...style,
        };

        return (
            <div style={actualStyle}>
                <Checkbox checked={checked} onChange={handleChange}>
                    {appSettingsData?.settings?.sendReversalInvoice === true
                        ? i18n.containers.bookings.BookingList.bookingReversal.sendReversalInvoice.overrideDoNotSendReversalInvoice()
                        : i18n.containers.bookings.BookingList.bookingReversal.sendReversalInvoice.overrideSendReversalInvoice()}
                </Checkbox>
            </div>
        );
    };

    return (
        <Modal
            title={i18n.containers.bookings.BookingList.bookingReversal.modalTitle()}
            open={open}
            onCancel={() => handleCancel()}
            destroyOnClose
            onOk={handleOk}
            footer={[
                <Button key="cancelReversalInvoice" onClick={() => handleCancel()}>
                    {i18n.containers.bookings.BookingList.bookingReversal.cancel()}
                </Button>,
                <Button
                    key="okReversalInvoice"
                    type="primary"
                    danger
                    onClick={handleOk}
                    // loading={cancelBookingAndCreateQueryState.loading}
                    loading={loading}
                    disabled={refundChoiceMissing}
                    data-testid="reverse-booking-confirm"
                >
                    {!email
                        ? i18n.containers.bookings.BookingList.bookingReversal.cancelNow()
                        : sendReversalInvoice
                        ? i18n.containers.bookings.BookingList.bookingReversal.cancelWithNotification()
                        : i18n.containers.bookings.BookingList.bookingReversal.cancelWithoutNotification()}
                </Button>,
            ]}
        >
            <Alert
                type="warning"
                message={<div className={css.header}>{i18n.containers.bookings.BookingList.bookingReversal.messageHeader()}</div>}
                description={
                    <div className={css.content}>
                        <p>{i18n.containers.bookings.BookingList.bookingReversal.messageP1()}</p>
                        <p>{i18n.containers.bookings.BookingList.bookingReversal.messageP2()}</p>
                    </div>
                }
            />
            {email && (
                <>
                    <Divider />
                    <Alert
                        type="warning"
                        message={<div className={css.header}>{i18n.containers.bookings.BookingList.bookingReversal.sendReversalInvoice.header()}</div>}
                        description={
                            <div className={css.content}>
                                {email &&
                                    (appSettingsData?.settings?.sendReversalInvoice === true ? (
                                        <p>{i18n.containers.bookings.BookingList.bookingReversal.sendReversalInvoice.messageP2Active()}</p>
                                    ) : (
                                        <p>{i18n.containers.bookings.BookingList.bookingReversal.sendReversalInvoice.messageP2InActive()}</p>
                                    ))}
                            </div>
                        }
                    />

                    <div
                        style={{
                            textAlign: "center",
                            fontSize: "0.9em",
                            color: colorGrey,
                            marginTop: 20,
                        }}
                    >
                        <p>{i18n.containers.bookings.BookingList.bookingReversal.sendReversalInvoice.messageP3()}</p>
                        <Link to={`${URL_SETTINGS_GENERAL}/buchungen`} style={{padding: "5px 12px", fontSize: "1em"}} onMouseDown={(e) => e.preventDefault()}>
                            {i18n.containers.bookings.BookingList.bookingReversal.sendReversalInvoice.toAppSettings()}
                        </Link>
                    </div>
                    <NotificationSelection style={{marginTop: 20}} />
                </>
            )}
            {/* Phase 14 / D-07: Inline Refund-Panel vor Confirm (Storno-Flow) */}
            {mollieRefundableSps.length > 0 && (
                <>
                    <Divider />
                    <RefundPanel
                        mode="storno"
                        scheduledPayments={mollieRefundableSps}
                        onSelectionChange={(selection) => setPendingRefunds(selection)}
                    />
                </>
            )}
        </Modal>
    );
};

export default memo(ReverseBookingModal);
